import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { API } from '@/App';
import axios from 'axios';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Brain, Leaf, Shield, ChevronDown, ChevronUp, RefreshCw,
  CheckCircle2, User, HeartPulse, Dna, Save,
} from 'lucide-react';
import { toast } from 'sonner';

const SECTION_ICONS = {
  identity_condition: User, symptom_intelligence: HeartPulse, symptom_verification: Shield,
  medication_response: HeartPulse, biological_terrain: Leaf, exposure_history: Dna,
  family_pattern: Dna, clinical_notes: Brain, biological_interpretation: Brain,
  biological_profile: Leaf, emotional_profile: HeartPulse, family_personal_profile: User,
  environmental_profile: Leaf, professional_lifestyle_profile: User,
};

export default function BioContextV2Page() {
  const [subscribers, setSubscribers] = useState([]);
  const [selectedSub, setSelectedSub] = useState('');
  const [schema, setSchema] = useState(null);
  const [context, setContext] = useState(null);
  const [universal, setUniversal] = useState({});
  const [extended, setExtended] = useState({});
  const [conditionData, setConditionData] = useState({});
  const [expanded, setExpanded] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/users`).then(r => {
        const users = Array.isArray(r.data) ? r.data : r.data?.users || [];
        setSubscribers(users.filter(u => u.role === 'subscriber'));
      }),
      axios.get(`${API}/biocontext/schema`).then(r => setSchema(r.data)),
    ]).catch(() => {});
  }, []);

  const loadContext = useCallback(async (subId) => {
    if (!subId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/biocontext/${subId}`);
      setContext(res.data);
      setUniversal(res.data.universal || {});
      setExtended(res.data.extended_profiles || {});
      setConditionData(res.data.condition_data || {});
    } catch { setContext(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { if (selectedSub) loadContext(selectedSub); }, [selectedSub, loadContext]);

  const handleSave = async (isComplete = false) => {
    if (!selectedSub) return;
    setSaving(true);
    try {
      await axios.post(`${API}/biocontext/${selectedSub}`, {
        universal, extended_profiles: extended, condition_data: conditionData, is_complete: isComplete,
      });
      toast.success(isComplete ? 'Biological context finalized' : 'Saved');
      loadContext(selectedSub);
    } catch (e) { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const toggle = (key) => setExpanded(p => ({ ...p, [key]: !p[key] }));

  const SectionEditor = ({ sectionKey, label, description, data, setData, icon: Icon }) => {
    const isOpen = expanded[sectionKey];
    const hasContent = data[sectionKey] && data[sectionKey].trim?.();
    return (
      <div className={`border rounded-lg transition-all ${isOpen ? 'border-white/10 bg-white/[0.02]' : 'border-white/[0.04]'}`}>
        <div className="flex items-center justify-between p-3 cursor-pointer" onClick={() => toggle(sectionKey)}>
          <div className="flex items-center gap-2.5">
            <div className="w-6 h-6 rounded flex items-center justify-center bg-white/[0.04]">
              {Icon ? <Icon className="w-3 h-3 text-white/40" /> : <Brain className="w-3 h-3 text-white/40" />}
            </div>
            <span className="text-xs font-medium text-white/70">{label}</span>
            {hasContent && <CheckCircle2 className="w-3 h-3 text-emerald-400/50" />}
          </div>
          {isOpen ? <ChevronUp className="w-3.5 h-3.5 text-white/20" /> : <ChevronDown className="w-3.5 h-3.5 text-white/20" />}
        </div>
        {isOpen && (
          <div className="px-3 pb-3">
            <p className="text-[10px] text-white/30 mb-2">{description}</p>
            <Textarea className="bg-white/[0.03] border-white/[0.06] text-white text-xs min-h-[80px]"
              placeholder={`Enter ${label.toLowerCase()} details...`}
              value={data[sectionKey] || ''}
              onChange={e => setData(p => ({ ...p, [sectionKey]: e.target.value }))} />
          </div>
        )}
      </div>
    );
  };

  return (
    <AdminLayout>
      <div data-testid="biocontext-v2-page" className="p-6 max-w-5xl mx-auto space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white tracking-tight">Biological Context</h1>
            <p className="text-sm text-white/40 mt-0.5">Universal core + condition-specific overlays</p>
          </div>
          {selectedSub && (
            <div className="flex gap-2">
              <Button data-testid="biocontext-save" size="sm" variant="outline" onClick={() => handleSave(false)} disabled={saving}
                className="border-white/10 text-white/60"><Save className="w-3.5 h-3.5 mr-1" />{saving ? 'Saving...' : 'Save Draft'}</Button>
              <Button data-testid="biocontext-finalize" size="sm" onClick={() => handleSave(true)} disabled={saving}
                className="bg-emerald-600 hover:bg-emerald-700 text-white">Finalize</Button>
            </div>
          )}
        </div>

        {/* Subscriber Selector */}
        <Select value={selectedSub} onValueChange={setSelectedSub}>
          <SelectTrigger data-testid="biocontext-sub-select" className="bg-white/[0.03] border-white/[0.06] text-white">
            <SelectValue placeholder="Select subscriber..." />
          </SelectTrigger>
          <SelectContent className="bg-[#0d1117] border-white/10 max-h-[300px]">
            {subscribers.map(s => <SelectItem key={s.id} value={s.id} className="text-white text-xs">{s.name} ({s.conditions?.join(', ') || 'N/A'})</SelectItem>)}
          </SelectContent>
        </Select>

        {loading && <div className="text-center py-8"><RefreshCw className="w-4 h-4 text-white/20 animate-spin mx-auto" /></div>}

        {selectedSub && schema && !loading && (
          <>
            {context?.is_complete && (
              <div className="flex items-center gap-2 p-3 bg-emerald-900/10 rounded-lg border border-emerald-800/20">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                <span className="text-xs text-emerald-400">Biological context finalized</span>
              </div>
            )}

            {/* Universal Sections */}
            <div>
              <p className="text-[10px] text-white/30 uppercase tracking-wider mb-2">Universal Core Sections</p>
              <div className="space-y-1">
                {schema.universal_sections.map(s => (
                  <SectionEditor key={s.key} sectionKey={s.key} label={s.label} description={s.description}
                    data={universal} setData={setUniversal} icon={SECTION_ICONS[s.key]} />
                ))}
              </div>
            </div>

            {/* Extended Profiles */}
            <div>
              <p className="text-[10px] text-white/30 uppercase tracking-wider mb-2">Extended Profiles</p>
              <div className="space-y-1">
                {schema.extended_profiles.map(s => (
                  <SectionEditor key={s.key} sectionKey={s.key} label={s.label} description={s.description}
                    data={extended} setData={setExtended} icon={SECTION_ICONS[s.key]} />
                ))}
              </div>
            </div>

            {/* Condition Overlays */}
            {context?.applicable_overlays && Object.keys(context.applicable_overlays).length > 0 && (
              <div>
                <p className="text-[10px] text-white/30 uppercase tracking-wider mb-2">Condition-Specific Overlays</p>
                {Object.entries(context.applicable_overlays).map(([condKey, condDef]) => (
                  <div key={condKey} className="mb-3">
                    <div className="flex items-center gap-2 mb-1.5">
                      <Badge className="bg-rose-800/20 text-rose-300 text-[9px]">{condDef.label}</Badge>
                    </div>
                    <div className="space-y-1">
                      {condDef.sections.map(s => (
                        <SectionEditor key={s.key} sectionKey={s.key} label={s.label} description={s.description}
                          data={conditionData} setData={setConditionData} icon={SECTION_ICONS[s.key]} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
